/* START OF FILE js/game.js */
/*
 * Filename: js/game.js
 * Purpose: Main game logic, UI interaction, and event handling for Squdle.
 */

// --- Import all necessary modules ---
import StorageManager from './StorageManager.js';
import AccessibilityManager from './core/AccessibilityManager.js';
import AudioManager from './AudioManager.js';
import AnimationManager from './AnimationManager.js';
import GameCore from './GameCore.js';
import GameUI from './GameUI.js';
import ModalManager from './ModalManager.js';
import InputHandler from './InputHandler.js';
import TimerManager from './TimerManager.js';
import PlayerStats from './stats.js';
import { initTelemetry, track, touchSessionOnSwap } from './telemetry.js';
import { puzzleLoadedRestoredPayload } from './telemetryPuzzleLoaded.js';
// Import core logic needed directly by game.js or event listeners if any
// (Currently, core logic seems encapsulated within classes)
// Note: SqudlePuzzle is imported within GameCore

document.addEventListener('DOMContentLoaded', async () => {
  console.log('DOM fully loaded, initializing Squdle...');

  // --- 1. Initialization & Dependency Check ---
  // Module imports handle dependency loading implicitly. Error handling happens at load time.
  // Remove the old dependency check based on window globals.

  // --- 2. Instantiate Core Components ---
  // Telemetry first: its prior-player check must run before any component writes squdle-* keys (spec §4).
  initTelemetry();

  // Order matters for dependencies
  const storageManager = new StorageManager();
  const accessibilityManager = new AccessibilityManager();
  const audioManager = new AudioManager(accessibilityManager);
  const animationManager = new AnimationManager(accessibilityManager);
  const gameCore = new GameCore(storageManager);
  const gamePlayerStats = new PlayerStats(storageManager, gameCore.gridSize);
  const gameUI = new GameUI(gameCore, accessibilityManager);
  // Pass AnimationManager to InputHandler
  const inputHandler = new InputHandler(gameCore, gameUI, accessibilityManager, animationManager);
  // Pass PlayerStats to ModalManager
  const modalManager = new ModalManager(gameCore, gameUI, accessibilityManager, storageManager, gamePlayerStats);
  inputHandler.setModalManager(modalManager); // Inject ModalManager into InputHandler
  gameCore.setPlayerStats(gamePlayerStats); // Inject PlayerStats into GameCore
  gameUI.setAnimationManager(animationManager); // Inject AnimationManager into GameUI
  const timerManager = new TimerManager();

  modalManager.setMessageDisplayFunction(gameUI.showMessage.bind(gameUI));

  // --- Remove global assignments ---
  // If debugging requires global access, uncomment these lines temporarily
  // window.gameCore = gameCore;
  // window.gameUI = gameUI;
  // window.modalManager = modalManager;
  // window.animationManager = animationManager;
  // window.gamePlayerStats = gamePlayerStats;
  // window.audioManager = audioManager;

  // --- 3. Load Initial State & Settings ---
  accessibilityManager.loadSettings();
  accessibilityManager.applySettings();
  const initialSettings = storageManager.loadSettings(); // Load settings including hideInstructions

  // --- 4. UI Element Caching and Setup ---
  const mainElement = document.querySelector('main'); // Cache the main element
  const gridSizeBtn4 = document.getElementById('grid-size-btn-4');
  const gridSizeBtn5 = document.getElementById('grid-size-btn-5');
  const gridSizeButtons = [gridSizeBtn4, gridSizeBtn5];
  const hardModeCheckbox = document.getElementById('hard-mode-checkbox');
  const resetButton = document.getElementById('reset-button'); // This is hidden via CSS
  const hintButton = document.getElementById('hint-button');

  // Menu elements
  const menuToggleButton = document.getElementById('menu-toggle-button');
  const menuCloseButton = document.getElementById('menu-close-button'); // Added close button
  const slideoutMenu = document.getElementById('slideout-menu');
  const menuOverlay = document.getElementById('menu-overlay');
  const menuNav = slideoutMenu?.querySelector('.menu-nav');

  // New checkbox element
  const hideInstructionsToggle = document.getElementById('hide-instructions-toggle');
  const hideInstructionsLabel = document.getElementById('hide-instructions-label');

  let menuIsOpen = false;
  let previouslyFocusedElement = null;

  const initialGridSize = gameCore.gridSize;

  // Function to update the visual state of the grid size buttons
  const updateActiveButton = (size) => {
    gridSizeButtons.forEach(btn => {
      if (btn) {
        const isActive = parseInt(btn.dataset.size) === size;
        btn.classList.toggle('active', isActive);
        btn.setAttribute('aria-checked', isActive ? 'true' : 'false');
        btn.tabIndex = isActive ? 0 : -1;
      }
    });
  };
  updateActiveButton(initialGridSize); // Set initial button state

  const trackRestoredPuzzleLoaded = () => {
    const puzzleDate = storageManager.todayDate;
    track('puzzle_loaded', puzzleLoadedRestoredPayload(
      puzzleDate,
      gameCore.gridSize,
      gameCore.isHardMode,
      gameCore.isSolved(),
      storageManager.hasCompletedTodaysPuzzle(gameCore.gridSize, puzzleDate),
    ));
  };

  // --- Apply initial Hide Instructions state ---
  if (hideInstructionsToggle && mainElement) {
    hideInstructionsToggle.checked = initialSettings.hideInstructions;
    mainElement.classList.toggle('instructions-hidden', initialSettings.hideInstructions);
    hideInstructionsLabel?.setAttribute('aria-checked', String(initialSettings.hideInstructions));
  }

  // --- Puzzle Fetch Status (must register before init to catch events) ---
  document.addEventListener('puzzle-fetch-status', async (e) => {
    const { status } = e.detail;
    switch (status) {
      case 'fetching':
        gameUI.hideRetryButton();
        gameUI.clearMessage();
        break;
      case 'loaded':
        gameUI.renderGrid();
        inputHandler.attachEventHandlers();
        track('puzzle_loaded', {
          grid_size: gameCore.gridSize,
          puzzle_date: storageManager.todayDate,
          mode: gameCore.isHardMode ? 'hard' : 'normal',
          restored: 0,
        });
        break;
      case 'retrying':
        gameUI.showMessage('Having trouble reaching today\'s puzzle. Trying again...', 0);
        break;
      case 'failed':
        gameUI.setGridBusy(false);
        gameUI.stopSkeletonPulse();
        if (gameCore.consumeDouble503()) {
          gameUI.showMessage('No puzzles available today. Check back later.', 0);
        } else {
          gameUI.showMessage('Today\'s puzzle isn\'t available right now. Check back soon.', 0);
        }
        gameUI.showRetryButton();
        break;
      case 'offline': {
        gameUI.setGridBusy(false);
        gameUI.stopSkeletonPulse();
        gameUI.hideRetryButton();

        // Try restoring from localStorage (AC2, AC3)
        const initResult = await gameCore.initGame();
        if (initResult.restored) {
          // FR15/FR16: Restore silently — no error message
          gameUI.renderGrid();
          inputHandler.attachEventHandlers();
          trackRestoredPuzzleLoaded();
          // _fetchState is 'idle' after initGame() — online handler won't refetch (AC7)
        } else {
          // FR17: No saved state
          gameUI.showMessage('You\'re offline. Connect to the internet to play today\'s puzzle.', 0);
          // _fetchState stays 'offline' — online handler WILL refetch (AC5)
          gameCore.setOfflineState();
        }
        break;
      }
    }
  });

  // --- Puzzle Fetch Redirect (503 handling) ---
  document.addEventListener('puzzle-fetch-redirect', (e) => {
    const { fromSize, toSize } = e.detail;
    gameUI.showRedirectMessage(fromSize, toSize);
  });

  // --- Puzzle Retry (manual retry from failed state) ---
  document.addEventListener('puzzle-retry-requested', async () => {
    if (gameCore._fetchState === 'fetching') return;
    gameUI.renderSkeleton(gameCore.gridSize);
    await gameCore.fetchAndInitPuzzle();
  });

  // --- Online/Offline event listeners ---
  window.addEventListener('online', () => {
    if (gameCore._fetchState === 'offline') {
      gameUI.renderSkeleton(gameCore.gridSize);
      gameCore.fetchAndInitPuzzle();
    }
  });

  window.addEventListener('offline', () => {
    gameCore.handleOffline();
  });

  // --- 5. Initialize Game and UI ---
  console.log(`Initializing game with grid size ${initialGridSize}`);
  const initResult = await gameCore.initGame();
  if (initResult.restored) {
    trackRestoredPuzzleLoaded();
  }
  if (initResult.restored) {
    gameUI.renderGrid();
    inputHandler.attachEventHandlers();
  } else {
    gameUI.renderSkeleton(gameCore.gridSize);
    await gameCore.fetchAndInitPuzzle();
    // attachEventHandlers() called by puzzle-fetch-status listener on 'loaded'
  }
  timerManager.startTimer();

  // Debug exposure for manual verification
  if (typeof window !== 'undefined') {
    window.__debug = { gameCore };
  }

  // --- 6. Welcome / Restored Messages & Visit Tracking ---
  const trackVisit = (lastPlayedDate) => {
    if (lastPlayedDate !== gameCore.todayDate) {
      if (!gameCore.hasCompletedTodaysPuzzle()) {
        gameUI.showMessage(`Welcome to today's Squdle! ${gameCore.MAX_SWAPS} swaps allowed in Hard Mode.`, 4000);
        // Use the instantiated gamePlayerStats directly
        gamePlayerStats.recordVisit(gameCore.todayDate, gameCore.gridSize);
      } else {
        gameUI.showMessage(`Welcome back! Today's ${gameCore.gridSize}x${gameCore.gridSize} puzzle is already completed.`, 4000);
      }
      storageManager.saveSettings({ lastPlayedDate: gameCore.todayDate });
    } else if (gameCore.restored && !gameCore.hasCompletedTodaysPuzzle()) {
      gameUI.announceToScreenReader(`Restored your previous ${gameCore.gridSize}x${gameCore.gridSize} puzzle progress.`);
    }
  };
  trackVisit(initialSettings.lastPlayedDate); // Use already loaded settings

  // Revalidation replaced a saved puzzle from another day: reset day-scoped UI and bookkeeping
  document.addEventListener('puzzle-date-changed', () => {
    modalManager.closeAllModals();
    inputHandler.clearSelection();
    trackVisit(storageManager.loadSettings().lastPlayedDate);
    gamePlayerStats.loadStatsForSize(gameCore.gridSize);
    gamePlayerStats.checkForStreakBreak(gameCore.todayDate, gameCore.gridSize, gamePlayerStats.data);
  });
  gameCore.watchForNewDay();

  if (gameCore.restored && gameCore.isSolved() && gameCore.hasCompletedTodaysPuzzle()) {
    console.log("Restored state is completed, showing victory modal.");
    const restoredDate = gameCore.todayDate;
    setTimeout(() => {
      if (!gameCore.restored || gameCore.todayDate !== restoredDate) return;
      modalManager.showModal('victory', { isRestoredState: true, isHardMode: gameCore.isHardMode });
    }, 500);
  }

  // --- 7. Event Listeners & Integration ---

  // --- Puzzle Events ---
  document.addEventListener('puzzle-solved', (e) => {
    const { isUnlimitedMode, isHardMode } = e.detail;
    track('puzzle_solved', {
      grid_size: gameCore.gridSize,
      puzzle_date: storageManager.todayDate,
      mode: isHardMode ? 'hard' : 'normal',
    });
    console.log('Event: puzzle-solved received', e.detail);
    modalManager.showModal('victory', { isRestoredState: false, isHardMode: isHardMode });
    animationManager.createFireworks(isHardMode);
    audioManager.playSound('firework');
    modalManager.updateLeaderboard(gameCore.gridSize);
    gameUI.updateControlStates();
  });

  document.addEventListener('puzzle-reset-request', () => {
    console.log('Event received: puzzle-reset-request');
    const performReset = async () => {
      console.log('Performing puzzle reset...');
      gameCore.clearRetryState();
      storageManager.clearGameState(gameCore.gridSize);
      const settings = storageManager.loadSettings();
      gameCore.isHardMode = settings.hardModePrefs?.[gameCore.gridSize] || false;
      gameCore.isUnlimitedMode = !gameCore.isHardMode;
      const result = await gameCore.initGame();
      if (result.restored) {
        gameUI.renderGrid();
        inputHandler.attachEventHandlers();
      } else {
        gameUI.renderSkeleton(gameCore.gridSize);
        await gameCore.fetchAndInitPuzzle();
        // attachEventHandlers() called by puzzle-fetch-status listener on 'loaded'
      }
      modalManager.closeAllModals();
      gameUI.announceToScreenReader('Puzzle reset.', true);
    };

    if (gameCore.puzzle && gameCore.puzzle.swapCount > 0 && !gameCore.isSolved() && !gameCore.hasCompletedTodaysPuzzle()) {
      modalManager.showResetConfirmation(performReset);
    } else {
      performReset();
    }
  });

  document.addEventListener('puzzle-hint-request', () => {
    console.log('Event received: puzzle-hint-request');
    const hintApplied = gameCore.applyHint();
    if (!hintApplied) {
      let message = 'Hint could not be applied.';
      if (gameCore.isHardMode) message = 'Hints unavailable in Hard Mode.';
      else if (gameCore.hintUsed) message = 'Hint already used.';
      else if (gameCore.isSolved()) message = 'Puzzle already solved.';
      gameUI.showMessage(message, 3000);
      gameUI.announceToScreenReader('Hint could not be found or applied.', true);
    }
  });

  document.addEventListener('hint-applied', (e) => {
    const { hint } = e.detail;
    console.log('Event received: hint-applied', hint);
    gameUI.renderGrid(); // Re-render to update swap count
    inputHandler.attachEventHandlers();
    gameUI.highlightHintTile(hint.targetRow, hint.targetCol);
    gameUI.announceToScreenReader(`Hint applied. ${gameCore.puzzle.swapCount} swaps used.`, true);
  });

  // --- Hard Mode Events ---
  hardModeCheckbox?.addEventListener('change', () => {
    const wantsToEnable = hardModeCheckbox.checked;
    console.log(`Hard mode checkbox changed. User wants enabled: ${wantsToEnable}`);
    // Directly call setHardMode. It now handles logic internally and returns true/false.
    // We don't need to handle 'confirmation_needed' anymore.
    // The 'hardmode-state-changed' and 'hardmode-toggle-failed' events
    // will still fire from GameCore to update the UI accordingly.
    const success = gameCore.setHardMode(wantsToEnable, false); // force = false

    if (!success) {
      // If setting failed (e.g., already started), ensure the checkbox
      // visually matches the actual game state. The 'hardmode-toggle-failed'
      // event listener below also handles this, but this provides immediate feedback.
      if (hardModeCheckbox) {
        hardModeCheckbox.checked = gameCore.isHardMode;
      }
    }
  });

  document.addEventListener('hardmode-state-changed', (e) => {
    const { isHardMode } = e.detail;
    console.log(`Event received: hardmode-state-changed, isHardMode: ${isHardMode}`);
    gameUI.updateControlStates();
    gameUI.showMessage(`Hard Mode ${isHardMode ? 'enabled' : 'disabled'}.`, 2000);
    gameUI.announceToScreenReader(`Hard mode ${isHardMode ? 'enabled' : 'disabled'}.`, true);
  });

  document.addEventListener('hardmode-toggle-failed', (e) => {
    const { reason } = e.detail;
    console.log(`Event received: hardmode-toggle-failed, reason: ${reason}`);
    if (hardModeCheckbox) { hardModeCheckbox.checked = gameCore.isHardMode; }
    let message = 'Could not change Hard Mode setting.';
    if (reason === 'already_started') { message = 'Hard Mode can only be changed before the first swap.'; }
    else if (reason === 'already_completed') { message = `Cannot enable Hard Mode, ${gameCore.gridSize}x${gameCore.gridSize} puzzle already completed today.`; }
    if (reason !== 'user_cancel') {
      gameUI.showMessage(message, 3000); gameUI.announceToScreenReader(message, true);
    }
    gameUI.updateControlStates(); // Ensure checkbox reflects actual state
  });

  document.addEventListener('hardmode-failed', (e) => {
    const { reason } = e.detail;
    console.log(`Event received: hardmode-failed, reason: ${reason}`);
    if (modalManager.isModalOpen()) return;
    modalManager.showHardModeFailModal(
      gameCore.MAX_SWAPS,
      () => { document.dispatchEvent(new CustomEvent('puzzle-reset-request')); }, // Try again resets
      () => {
        gameCore.enableUnlimitedMode(); // This now calls setHardMode(false, true)
        gameUI.showMessage('Switched to Regular Mode. Continuing with unlimited swaps.', 3000);
      }
    );
  });

  // --- UI Interaction Listeners ---
  gridSizeButtons.forEach(button => {
    if (button) {
      button.addEventListener('click', (e) => {
        const newSize = parseInt(e.currentTarget.dataset.size);
        if (newSize !== gameCore.gridSize && (newSize === 4 || newSize === 5)) {
          document.dispatchEvent(new CustomEvent('puzzle-size-switch-requested', {
            detail: { toSize: newSize }
          }));
        }
      });
    }
  });

  // --- Size Switch Handler (event-driven with reentrant guard) ---
  let _switchInProgress = false;

  document.addEventListener('puzzle-size-switch-requested', async (e) => {
    const newSize = e.detail.toSize;
    if (_switchInProgress) return;
    if (newSize === gameCore.gridSize) return;
    if (newSize !== 4 && newSize !== 5) return;

    _switchInProgress = true;
    try {
      gameCore.saveGameState();
      gameCore.clearRetryState();
      gameCore.setGridSize(newSize);
      const result = await gameCore.initGame();

      if (result.restored) {
        gameUI.renderGrid();
        inputHandler.attachEventHandlers();
        trackRestoredPuzzleLoaded();
      } else {
        gameUI.renderSkeleton(newSize);
        await gameCore.fetchAndInitPuzzle();
        // attachEventHandlers() called by puzzle-fetch-status listener on 'loaded'
      }

      updateActiveButton(newSize);
      gamePlayerStats.currentGridSize = newSize;
      gamePlayerStats.loadStatsForSize(newSize);
      gameUI.announceToScreenReader(`Switched to ${newSize}x${newSize} puzzle.`);
    } finally {
      _switchInProgress = false;
    }
  });

  hintButton?.addEventListener('click', () => {
    document.dispatchEvent(new CustomEvent('puzzle-hint-request'));
  });

  hideInstructionsToggle?.addEventListener('change', () => {
    const isChecked = hideInstructionsToggle.checked;
    if (mainElement) {
      mainElement.classList.toggle('instructions-hidden', isChecked);
    }
    storageManager.saveSettings({ hideInstructions: isChecked });
    hideInstructionsLabel?.setAttribute('aria-checked', String(isChecked));
    gameUI.announceToScreenReader(`Instructions ${isChecked ? 'hidden' : 'shown'}.`, false);
    setTimeout(() => hideInstructionsToggle?.focus(), 0);
  });

  // --- Menu Listeners ---
  const openMenu = () => {
    if (!slideoutMenu || !menuOverlay || !menuToggleButton || menuIsOpen) return;
    previouslyFocusedElement = document.activeElement;
    slideoutMenu.classList.add('open');
    menuOverlay.classList.add('open');
    menuToggleButton.setAttribute('aria-expanded', 'true');
    slideoutMenu.setAttribute('aria-hidden', 'false');
    menuIsOpen = true;
    accessibilityManager?.announce('Menu opened', false);
    setTimeout(() => {
      const firstFocusable = slideoutMenu.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      firstFocusable?.focus();
    }, 50);
  };

  const closeMenu = () => {
    if (!slideoutMenu || !menuOverlay || !menuToggleButton || !menuIsOpen) return;
    // Move focus out before hiding to prevent aria-hidden focus warning
    if (slideoutMenu.contains(document.activeElement)) {
      document.activeElement.blur();
    }
    slideoutMenu.classList.remove('open');
    menuOverlay.classList.remove('open');
    menuToggleButton.setAttribute('aria-expanded', 'false');
    slideoutMenu.setAttribute('aria-hidden', 'true');
    menuIsOpen = false;
    accessibilityManager?.announce('Menu closed', false);
    if (previouslyFocusedElement && typeof previouslyFocusedElement.focus === 'function') {
        previouslyFocusedElement.focus();
    } else {
        menuToggleButton?.focus();
    }
    previouslyFocusedElement = null;
  };

  menuToggleButton?.addEventListener('click', () => { menuIsOpen ? closeMenu() : openMenu(); });
  menuCloseButton?.addEventListener('click', closeMenu);
  menuOverlay?.addEventListener('click', closeMenu);

  const handleMenuEscape = (event) => {
    if (event.key === 'Escape' && menuIsOpen && !modalManager.isModalOpen()) {
      event.preventDefault();
      closeMenu();
    }
  };
  document.addEventListener('keydown', handleMenuEscape);

  slideoutMenu?.addEventListener('keydown', (event) => {
    if (!menuIsOpen || event.key !== 'Tab') return;
    const focusableElements = Array.from(
        slideoutMenu.querySelectorAll('button, [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])')
      ).filter(el => el.offsetParent !== null);
    if (focusableElements.length === 0) return;
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    if (event.shiftKey) { if (document.activeElement === firstElement) { lastElement.focus(); event.preventDefault(); } }
    else { if (document.activeElement === lastElement) { firstElement.focus(); event.preventDefault(); } }
  });

  slideoutMenu?.querySelectorAll('button[role="menuitem"]').forEach(item => {
    item.addEventListener('click', () => { previouslyFocusedElement = item; setTimeout(closeMenu, 50); });
  });
  hideInstructionsLabel?.addEventListener('click', (e) => {
      if (e.target !== hideInstructionsToggle) { previouslyFocusedElement = hideInstructionsLabel; setTimeout(closeMenu, 50); }
  });


  // --- Global Keydown Listener ---
  document.addEventListener('keydown', inputHandler.handleGlobalKeydown);

  // --- **NEW: Viewport Resize Listener** ---
  const mobileMediaQuery = window.matchMedia("(max-width: 600px)");

  function handleResizeChange(event) {
      console.log(`Viewport breakpoint crossed. Mobile: ${event.matches}`);
      // Re-render based on current state — skeleton if still fetching, grid if loaded
      if (gameCore._fetchState === 'fetching' || gameCore._fetchState === 'retrying') {
        gameUI.renderSkeleton(gameCore.gridSize);
      } else if (gameCore.puzzle) {
        gameUI.renderGrid();
        inputHandler.attachEventHandlers();
      }
  }

  // Add the listener for changes in viewport size across the breakpoint
  mobileMediaQuery.addEventListener('change', handleResizeChange);
  // --- End Viewport Resize Listener ---

  // --- 8. Final Setup Calls ---
  storageManager.clearStaleData();
  // Use the instantiated gamePlayerStats directly
  gamePlayerStats.loadStatsForSize(gameCore.gridSize); // Ensure stats for current size are loaded
  gamePlayerStats.checkForStreakBreak(gameCore.todayDate, gameCore.gridSize, gamePlayerStats.data); // Check streak

  console.log('Squdle Game Initialized Successfully');
  console.log('>>> DOMContentLoaded listener execution finished.');

}); // End DOMContentLoaded

// Konami code listener (keep as is for debugging)
const konamiCode = [ 'ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a', ];
let konamiIndex = 0;
document.addEventListener('keydown', (event) => {
    const activeEl = document.activeElement;
    if (activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA')) return;
    if (event.key.toLowerCase() === konamiCode[konamiIndex].toLowerCase()) {
      konamiIndex++;
      if (konamiIndex === konamiCode.length) {
        console.warn('KONAMI CODE DETECTED - Resetting daily tracking & preferences.');
        const dateStr = window.__debug?.gameCore?.todayDate || new Date().toISOString().split('T')[0];
        for (let s = 4; s <= 5; s++) {
          localStorage.removeItem(`squdle-completed-${s}x${s}-${dateStr}`);
          localStorage.removeItem(`squdle-current-state-${s}x${s}`);
          localStorage.removeItem(`squdle-last-swap-count-${s}x${s}`);
          localStorage.removeItem(`squdle-leaderboard-${s}x${s}-${dateStr}`);
          localStorage.removeItem(`squdle-player-stats-${s}x${s}`);
        }
        localStorage.removeItem('squdle-settings');
        localStorage.removeItem('squdle-accessibility');

        // Use instantiated gameUI if available
        if (gameUI) gameUI.showMessage('DEBUG: All daily tracking & preferences reset!', 3000);
        else alert('DEBUG: All daily tracking & preferences reset!');
        konamiIndex = 0;
        window.location.reload();
      }
    } else {
      konamiIndex = 0;
    }
});
/* END OF FILE js/game.js */
